import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatStudentDisplayName } from "@/modules/students/domain/lib/student-display-name";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { printStudentMembershipConfirmationA4 } from "@/shared/lib/student-membership-confirmation-print-a4";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  CalendarIcon,
  ClockIcon,
  EditIcon,
  IdCardIcon,
  MailIcon,
  PhoneIcon,
  PowerIcon,
  PrinterIcon,
  UserCogIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  useStudentActivePackagesQuery,
  useStudentActivePrivateLessonPackagesQuery,
  useStudentAttendanceQuery,
  useStudentAttendanceSummaryQuery,
  useStudentPackagesQuery,
  useStudentPrivateLessonBookingsQuery,
  useStudentPrivateLessonPackagesQuery,
  useStudentQuery,
  useStudentSummaryQuery,
  useToggleStudentStatus,
} from "../api/queries";
import { ActivePackageSection } from "../ui/ActivePackageSection";
import { ActivePrivateLessonPackageSection } from "../ui/ActivePrivateLessonPackageSection";
import { AttendanceHistoryFilter } from "../ui/AttendanceHistoryFilter";
import { AttendanceHistoryTable } from "../ui/AttendanceHistoryTable";
import { AttendanceSummaryCards } from "../ui/AttendanceSummaryCards";
import { PackageHistoryFilter } from "../ui/PackageHistoryFilter";
import {
  PackageHistoryTable,
  type PackageRow,
} from "../ui/PackageHistoryTable";
import { PrivateLessonBookingHistoryFilter } from "../ui/PrivateLessonBookingHistoryFilter";
import { PrivateLessonBookingHistoryTable } from "../ui/PrivateLessonBookingHistoryTable";
import { StudentMembershipSignedDocumentSection } from "../ui/StudentMembershipSignedDocumentSection";

function StudentProfilePage() {
  const nav = useNavigate({ from: "/app/students/$id" });
  const { id } = useParams({ from: "/app/students/$id" });
  const search = useSearch({ from: "/app/students/$id" });
  const { data: student, ...result } = useStudentQuery(id);
  const { data: summary } = useStudentSummaryQuery(id);
  const { data: activePackagesData } = useStudentActivePackagesQuery(id);
  const { data: activePrivateLessonPackagesData } =
    useStudentActivePrivateLessonPackagesQuery(id);
  const toggleStatus = useToggleStudentStatus(id);
  const [activeTab, setActiveTab] = useState("general");
  const autoPrintDoneRef = useRef(false);

  const handlePrintMembershipConfirmation = async () => {
    if (!student) return;
    try {
      await printStudentMembershipConfirmationA4({
        memberId: student.memberId,
        name: formatStudentDisplayName(student.name, student.nickname),
        phoneNumber: student.phoneNumber,
        email: student.email,
        dateOfBirth: student.dateOfBirth,
        gender: student.gender,
        groupName: student.groupName,
        registeredAt: student.createdAt,
        status: student.status,
      });
    } catch {
      toast.error("ພິມໃບຢືນຢັນສະມາຊິກບໍ່ສຳເລັດ");
    }
  };

  useEffect(() => {
    if (!student || autoPrintDoneRef.current) return;
    if (search.printMembership !== "1") return;
    autoPrintDoneRef.current = true;
    void handlePrintMembershipConfirmation().finally(() => {
      nav({
        search: (prev) => ({ ...prev, printMembership: undefined }),
        replace: true,
      });
    });
  }, [student, search.printMembership]);

  // Package History state (not synced with URL)
  const [packageHistoryOffset, setPackageHistoryOffset] = useState(0);
  const [packageHistoryLimit] = useState(20);
  const [packageHistorySort, setPackageHistorySort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);
  const [packageHistoryFilters, setPackageHistoryFilters] = useState<
    FilterConditionDTO[] | undefined
  >(undefined);

  const packageHistoryQuery = useStudentPackagesQuery(id, {
    offset: packageHistoryOffset,
    limit: packageHistoryLimit,
    sort: packageHistorySort,
    filters: packageHistoryFilters,
  });

  const privateLessonPackagesQuery = useStudentPrivateLessonPackagesQuery(id, {
    offset: packageHistoryOffset,
    limit: packageHistoryLimit,
    sort: packageHistorySort,
    filters: packageHistoryFilters,
  });

  // Attendance History state (not synced with URL)
  const [attendanceHistoryOffset, setAttendanceHistoryOffset] = useState(0);
  const [attendanceHistoryLimit] = useState(20);
  const [attendanceHistorySort, setAttendanceHistorySort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);
  const [attendanceHistoryFilters, setAttendanceHistoryFilters] = useState<
    FilterConditionDTO[] | undefined
  >(undefined);

  const attendanceHistoryQuery = useStudentAttendanceQuery(id, {
    offset: attendanceHistoryOffset,
    limit: attendanceHistoryLimit,
    sort: attendanceHistorySort,
    filters: attendanceHistoryFilters,
  });

  const attendanceSummaryQuery = useStudentAttendanceSummaryQuery(id);

  // Private Lesson Bookings state (not synced with URL)
  const [privateLessonBookingsOffset, setPrivateLessonBookingsOffset] =
    useState(0);
  const [privateLessonBookingsLimit] = useState(20);
  const [privateLessonBookingsSort, setPrivateLessonBookingsSort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);
  const [privateLessonBookingsFilters, setPrivateLessonBookingsFilters] =
    useState<FilterConditionDTO[] | undefined>(undefined);

  const privateLessonBookingsQuery = useStudentPrivateLessonBookingsQuery(id, {
    offset: privateLessonBookingsOffset,
    limit: privateLessonBookingsLimit,
    sort: privateLessonBookingsSort,
    filters: privateLessonBookingsFilters,
  });

  const canEdit = useActionPermission(["students:update"]);

  const handleToggleStatus = async () => {
    await toast.promise(toggleStatus.mutateAsync(), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: "ປ່ຽນສະຖານະສຳເລັດ",
      error: "ປ່ຽນສະຖານະລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກຮຽນ", to: "/app/students" },
            { label: "ໂປຣໄຟລ໌" },
          ]}
        />
      </Header>

      <Main>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດນັກຮຽນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!student ? null : (
            <div className="space-y-6">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between space-y-2">
                <div className="flex items-center gap-4">
                  <Avatar className="size-24">
                    <AvatarImage
                      src={resolveImageSrc(student.image) ?? undefined}
                      alt={student.name}
                    />
                    <AvatarFallback className="text-lg">
                      {getInitials(student.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-2">
                    <h1 className="font-bold text-3xl tracking-tight">
                      {student.name}
                    </h1>
                    {student.nickname ? (
                      <p className="text-muted-foreground text-sm">
                        ຊື່ຫຼີ້ນ: {student.nickname}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <IdCardIcon className="size-4" />
                      <span>ເລກທີ່ສະມາຊິກ: {student.memberId}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => void handlePrintMembershipConfirmation()}
                  >
                    <PrinterIcon className="size-4" />
                    ພິມໃບຢືນຢັນສະມາຊິກ
                  </Button>
                  {canEdit && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() =>
                          nav({
                            to: "/app/students/$id/edit",
                            params: { id: student.id },
                          })
                        }
                      >
                        <EditIcon className="size-4" />
                        ແກ້ໄຂນັກຮຽນ
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleToggleStatus}
                        disabled={toggleStatus.isPending}
                      >
                        <PowerIcon className="size-4" />
                        ປ່ຽນສະຖານະ
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Tabs Navigation */}
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <div className="overflow-x-auto">
                  <TabsList className="h-auto w-full min-w-max border-b bg-transparent p-0 sm:w-full">
                    <TabsTrigger
                      value="general"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ຂໍ້ມູນທົ່ວໄປ
                    </TabsTrigger>
                    <TabsTrigger
                      value="active-package"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ແພັກເກດທີ່ໃຊ້ງານ
                    </TabsTrigger>
                    <TabsTrigger
                      value="package-history"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ປະຫວັດແພັກເກດ
                    </TabsTrigger>
                    <TabsTrigger
                      value="attendance-history"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ປະຫວັດການເຂົ້າຮຽນ
                    </TabsTrigger>
                    <TabsTrigger
                      value="private-lesson-bookings"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ການຈອງຮຽນສ່ວນຕົວ
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* General Info Tab */}
                {activeTab === "general" && (
                  <div className="mt-6 space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="font-normal text-muted-foreground text-sm">
                            ແພັກເກດປັດຈຸບັນ
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {summary?.activePackages &&
                            summary.activePackages.length > 0 ? (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                {summary.activePackages.length} ແພັກເກດ
                              </p>
                              <div className="mt-2 space-y-1">
                                {summary.activePackages.map((pkg) => (
                                  <p
                                    key={pkg.id}
                                    className="text-muted-foreground text-xs"
                                  >
                                    {pkg.templateName} — ເຫຼືອ{" "}
                                    {pkg.remainingSessions} ຄັ້ງ • ໝົດອາຍຸ{" "}
                                    {formatDate(pkg.expireDate)}
                                  </p>
                                ))}
                              </div>
                            </>
                          ) : summary?.activePackage ? (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                {summary.activePackage.templateName}
                              </p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                ເຫຼືອ {summary.activePackage.remainingSessions}{" "}
                                ຄັ້ງ • ໝົດອາຍຸ{" "}
                                {formatDate(summary.activePackage.expireDate)}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                -
                              </p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                ບໍ່ມີແພັກເກດ
                              </p>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="font-normal text-muted-foreground text-sm">
                            ແພັກເກດຮຽນສ່ວນຕົວປັດຈຸບັນ
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {summary?.activePrivateLessonPackages &&
                            summary.activePrivateLessonPackages.length > 0 ? (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                {summary.activePrivateLessonPackages.length}{" "}
                                ແພັກເກດ
                              </p>
                              <div className="mt-2 space-y-1">
                                {summary.activePrivateLessonPackages.map(
                                  (pkg) => (
                                    <p
                                      key={pkg.id}
                                      className="text-muted-foreground text-xs"
                                    >
                                      {pkg.templateName} — ເຫຼືອ{" "}
                                      {pkg.remainingHours} ຊົ່ວໂມງ • ໝົດອາຍຸ{" "}
                                      {formatDate(pkg.expireDate)}
                                    </p>
                                  ),
                                )}
                              </div>
                            </>
                          ) : summary?.activePrivateLessonPackage ? (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                {
                                  summary.activePrivateLessonPackage
                                    .templateName
                                }
                              </p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                ເຫຼືອ{" "}
                                {
                                  summary.activePrivateLessonPackage
                                    .remainingHours
                                }{" "}
                                ຊົ່ວໂມງ • ໝົດອາຍຸ{" "}
                                {formatDate(
                                  summary.activePrivateLessonPackage.expireDate,
                                )}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="font-semibold text-destructive text-lg">
                                -
                              </p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                ບໍ່ມີແພັກເກດຮຽນສ່ວນຕົວ
                              </p>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="font-normal text-muted-foreground text-sm">
                            ຈຳນວນການເຂົ້າຮຽນທັງໝົດ
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="font-semibold text-lg">
                            {summary?.totalAttendance ?? 0} ຄັ້ງ
                          </p>
                          <p className="mt-1 text-muted-foreground text-xs">
                            {summary && summary.totalAttendance > 0
                              ? "ມີການເຂົ້າຮຽນແລ້ວ"
                              : "ຍັງບໍ່ມີການເຂົ້າຮຽນ"}
                          </p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="font-normal text-muted-foreground text-sm">
                            ການເຂົ້າຮຽນຄັ້ງລ່າສຸດ
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {summary?.lastVisit ? (
                            <>
                              <p className="font-semibold text-lg">
                                {formatDate(summary.lastVisit)}
                              </p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                {formatDateTime(summary.lastVisit)}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="font-semibold text-lg">-</p>
                              <p className="mt-1 text-muted-foreground text-xs">
                                ຍັງບໍ່ມີການເຂົ້າຮຽນ
                              </p>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader>
                        <CardTitle>ຂໍ້ມູນນັກຮຽນ</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          {/* Left Column */}
                          <div className="space-y-4">
                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ເລກທີ່ສະມາຊິກ
                              </div>
                              <p className="font-semibold">
                                {student.memberId}
                              </p>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ຊື່ເຕັມ
                              </div>
                              <div className="flex items-center gap-2">
                                <UserIcon className="size-4 text-destructive" />
                                <p className="font-semibold">{student.name}</p>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ເບີໂທ
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <PhoneIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {student.phoneNumber || "-"}
                                </p>
                                <Badge
                                  variant={
                                    student.whatsappOptIn
                                      ? "default"
                                      : "secondary"
                                  }
                                >
                                  {student.whatsappOptIn
                                    ? "WhatsApp opt-in"
                                    : "WhatsApp opt-out"}
                                </Badge>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ອີເມວ
                              </div>
                              <div className="flex items-center gap-2">
                                <MailIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {student.email || "-"}
                                </p>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ວັນເດືອນປີເກີດ
                              </div>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {student.dateOfBirth
                                    ? formatDate(student.dateOfBirth)
                                    : "-"}
                                </p>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ເພດ
                              </div>
                              <p className="font-semibold">
                                {student.gender == null
                                  ? "-"
                                  : student.gender === "male"
                                    ? "ຊາຍ"
                                    : student.gender === "female"
                                      ? "ຍິງ"
                                      : "ອື່ນໆ"}
                              </p>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ສະຖານະ
                              </div>
                              <div className="mt-1">
                                <Badge
                                  variant={
                                    student.status === "active"
                                      ? "default"
                                      : "secondary"
                                  }
                                >
                                  {student.status === "active"
                                    ? "ໃຊ້ງານ"
                                    : "ປິດການໃຊ້ງານ"}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="space-y-4">
                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ວັນທີ່ສ້າງ
                              </div>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {formatDate(student.createdAt)}
                                </p>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ສ້າງໂດຍ
                              </div>
                              <div className="flex items-center gap-2">
                                <UserCogIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {student.createdBy || "-"}
                                </p>
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground text-sm">
                                ອັບເດດລ່າສຸດ
                              </div>
                              <div className="flex items-center gap-2">
                                <ClockIcon className="size-4 text-destructive" />
                                <p className="font-semibold">
                                  {formatDate(student.updatedAt)}
                                </p>
                              </div>
                            </div>

                            {student.groupName && (
                              <div>
                                <div className="font-medium text-muted-foreground text-sm">
                                  ກຸ່ມ
                                </div>
                                <p className="font-semibold">
                                  {student.groupName}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Notes Section */}
                        {student.notes && (
                          <div className="mt-6 border-t pt-6">
                            <div className="font-medium text-muted-foreground text-sm">
                              ໝາຍເຫດ
                            </div>
                            <p className="mt-2 text-sm leading-relaxed">
                              {student.notes}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <StudentMembershipSignedDocumentSection
                      studentId={student.id}
                      fileId={student.membershipSignedDocumentFileId}
                      uploadedAt={student.membershipSignedDocumentUploadedAt}
                      canEdit={canEdit}
                    />
                  </div>
                )}

                {/* Active Package Tab */}
                {activeTab === "active-package" && (
                  <div className="mt-6 space-y-6">
                    <ActivePackageSection
                      studentId={student.id}
                      packages={activePackagesData?.packages ?? []}
                    />
                    <ActivePrivateLessonPackageSection
                      studentId={student.id}
                      packages={
                        activePrivateLessonPackagesData?.packages ?? []
                      }
                    />
                  </div>
                )}

                {/* Package History Tab */}
                {activeTab === "package-history" && (
                  <div className="mt-6">
                    <div className="flex flex-col rounded-xl border bg-card pt-2">
                      <PackageHistoryFilter
                        filters={packageHistoryFilters ?? []}
                        onFiltersChange={(filters) => {
                          setPackageHistoryFilters(filters);
                          setPackageHistoryOffset(0);
                        }}
                      />
                      <PackageHistoryTable
                        data={(() => {
                          const packageTypeFilter = packageHistoryFilters?.find(
                            (f) => f.field === "packageType",
                          );
                          const packageType =
                            (packageTypeFilter?.value as string) || "all";

                          const packages: PackageRow[] =
                            packageHistoryQuery.data?.data.map((p) => ({
                              ...p,
                              packageType: "package" as const,
                            })) ?? [];

                          const privateLessonPackages: PackageRow[] =
                            privateLessonPackagesQuery.data?.data.map((p) => ({
                              ...p,
                              packageType: "private_lesson_package" as const,
                            })) ?? [];

                          let combined: PackageRow[] = [];
                          if (packageType === "all") {
                            combined = [...packages, ...privateLessonPackages];
                          } else if (packageType === "package") {
                            combined = packages;
                          } else if (packageType === "private_lesson_package") {
                            combined = privateLessonPackages;
                          }

                          // Sort by purchasedAt descending (newest first)
                          combined.sort((a, b) => {
                            const dateA = new Date(a.purchasedAt).getTime();
                            const dateB = new Date(b.purchasedAt).getTime();
                            return dateB - dateA;
                          });

                          // Apply pagination
                          const start = packageHistoryOffset;
                          const end = start + packageHistoryLimit;
                          return combined.slice(start, end);
                        })()}
                        isLoading={
                          packageHistoryQuery.isLoading ||
                          privateLessonPackagesQuery.isLoading
                        }
                        offset={packageHistoryOffset}
                        limit={packageHistoryLimit}
                        totalCount={(() => {
                          const packageTypeFilter = packageHistoryFilters?.find(
                            (f) => f.field === "packageType",
                          );
                          const packageType =
                            (packageTypeFilter?.value as string) || "all";

                          if (packageType === "all") {
                            return (
                              (packageHistoryQuery.data?.meta?.total ?? 0) +
                              (privateLessonPackagesQuery.data?.meta?.total ??
                                0)
                            );
                          }
                          if (packageType === "package") {
                            return packageHistoryQuery.data?.meta?.total ?? 0;
                          }
                          return (
                            privateLessonPackagesQuery.data?.meta?.total ?? 0
                          );
                        })()}
                        onPaginationChange={(offset) => {
                          setPackageHistoryOffset(offset);
                        }}
                        sortBy={
                          packageHistorySort
                            ? packageHistorySort[0]?.field
                            : undefined
                        }
                        sortOrder={
                          packageHistorySort
                            ? (packageHistorySort[0]?.dir as "asc" | "desc")
                            : undefined
                        }
                        onSortingChange={(id, desc) => {
                          setPackageHistorySort([
                            { field: id, dir: desc ? "desc" : "asc" },
                          ]);
                        }}
                        onViewDetails={(pkg) => {
                          // TODO: Navigate to package details page when available
                          console.log("View package details:", pkg.id);
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Attendance History Tab */}
                {activeTab === "attendance-history" && (
                  <div className="mt-6 space-y-6">
                    {/* Summary Cards */}
                    <AttendanceSummaryCards
                      summary={attendanceSummaryQuery.data}
                      isLoading={attendanceSummaryQuery.isLoading}
                    />

                    {/* Filter and Table */}
                    <div className="flex flex-col rounded-xl border bg-card pt-2">
                      <AttendanceHistoryFilter
                        filters={attendanceHistoryFilters ?? []}
                        onFiltersChange={(filters) => {
                          setAttendanceHistoryFilters(filters);
                          setAttendanceHistoryOffset(0);
                        }}
                      />
                      <AttendanceHistoryTable
                        data={attendanceHistoryQuery.data?.data ?? []}
                        isLoading={attendanceHistoryQuery.isLoading}
                        offset={attendanceHistoryOffset}
                        limit={attendanceHistoryLimit}
                        totalCount={
                          attendanceHistoryQuery.data?.meta?.total ?? 0
                        }
                        onPaginationChange={(offset) => {
                          setAttendanceHistoryOffset(offset);
                        }}
                        sortBy={
                          attendanceHistorySort
                            ? attendanceHistorySort[0]?.field
                            : undefined
                        }
                        sortOrder={
                          attendanceHistorySort
                            ? (attendanceHistorySort[0]?.dir as "asc" | "desc")
                            : undefined
                        }
                        onSortingChange={(id, desc) => {
                          setAttendanceHistorySort([
                            { field: id, dir: desc ? "desc" : "asc" },
                          ]);
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Private Lesson Bookings Tab */}
                {activeTab === "private-lesson-bookings" && (
                  <div className="mt-6">
                    <div className="flex flex-col rounded-xl border bg-card pt-2">
                      <PrivateLessonBookingHistoryFilter
                        filters={privateLessonBookingsFilters ?? []}
                        onFiltersChange={(filters) => {
                          setPrivateLessonBookingsFilters(filters);
                          setPrivateLessonBookingsOffset(0);
                        }}
                      />
                      <PrivateLessonBookingHistoryTable
                        data={privateLessonBookingsQuery.data?.data ?? []}
                        isLoading={privateLessonBookingsQuery.isLoading}
                        offset={privateLessonBookingsOffset}
                        limit={privateLessonBookingsLimit}
                        totalCount={
                          privateLessonBookingsQuery.data?.meta?.total ?? 0
                        }
                        onPaginationChange={(offset) => {
                          setPrivateLessonBookingsOffset(offset);
                        }}
                        sortBy={
                          privateLessonBookingsSort
                            ? privateLessonBookingsSort[0]?.field
                            : undefined
                        }
                        sortOrder={
                          privateLessonBookingsSort
                            ? (privateLessonBookingsSort[0]?.dir as
                              | "asc"
                              | "desc")
                            : undefined
                        }
                        onSortingChange={(id, desc) => {
                          setPrivateLessonBookingsSort([
                            { field: id, dir: desc ? "desc" : "asc" },
                          ]);
                        }}
                        onViewDetails={(booking) => {
                          // TODO: Navigate to booking details page when available
                          console.log("View booking details:", booking.id);
                        }}
                      />
                    </div>
                  </div>
                )}
              </Tabs>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}

export { StudentProfilePage };
export default StudentProfilePage;
